import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Loader2,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';

import ReadOnlyNotice from '../access/ReadOnlyNotice';
import { useAccess } from '../../services/access';
import AttentionSummary from './AttentionSummary';
import {
  answerClarification,
  approveProposal,
  listClarifications,
  listProposals,
  rejectProposal,
  listAttentionReviews,
  reviewAttentionEntity,
  recordProposalDelivery,
} from '../../services/meetingApprovalsApi';

/**
 * The human end of the meeting pipeline, as a panel.
 *
 * Two queues, deliberately distinct: a proposal is a decision to take, a
 * clarification is a question to answer.
 *
 * Pass `meetingId` to scope both queues to one meeting — that is the meeting
 * detail view. Omit it for the whole project's queue.
 */

const KIND_STYLES = {
  VERIFICATION_REQUEST: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  ESCALATION: 'border-red-500/30 bg-red-500/10 text-red-400',
  CLOSURE_CONFIRMATION: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
  FOLLOW_UP: 'border-zinc-700 bg-zinc-800/60 text-zinc-300',
};

const Chip = ({ children, className = '' }) => (
  <span
    className={`inline-block rounded-lg border px-2 py-0.5 text-[11px] font-semibold ${className}`}
  >
    {children}
  </span>
);

/**
 * Owns both queues. Lifted out of the panel so a parent (the tab strip) can
 * show a count without fetching the same thing twice.
 */
export const useMeetingApprovals = (accessToken, meetingId = null) => {
  const [proposals, setProposals] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [attentionReviews, setAttentionReviews] = useState([]);
  const [approvedContacts, setApprovedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [
        proposalsResponse,
        clarificationsResponse,
        attentionResponse,
        approvedResponse,
      ] = await Promise.all([
        listProposals(accessToken, { sourceMeetingId: meetingId }),
        listClarifications(accessToken, { sourceMeetingId: meetingId }),
        // Not live on the backend yet -- caught here so its failure can't
        // take the other three queues down with it via Promise.all.
        listAttentionReviews(accessToken, { sourceMeetingId: meetingId }).catch(
          () => ({ entities: [] }),
        ),
        listProposals(accessToken, {
          sourceMeetingId: meetingId,
          status: 'APPROVED',
        }),
      ]);
      setProposals(proposalsResponse?.proposals || []);
      setClarifications(clarificationsResponse?.clarifications || []);
      setAttentionReviews(attentionResponse?.entities || []);
      setApprovedContacts(
        (approvedResponse?.proposals || []).filter(
          (proposal) =>
            !proposal.delivered_at &&
            ['FOLLOW_UP', 'ESCALATION', 'VERIFICATION_REQUEST'].includes(
              proposal.kind,
            ),
        ),
      );
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, meetingId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    proposals,
    setProposals,
    clarifications,
    setClarifications,
    attentionReviews,
    setAttentionReviews,
    approvedContacts,
    loading,
    error,
    setError,
    reload,
    pendingCount:
      proposals.length + clarifications.length + attentionReviews.length,
  };
};

const ProposalCard = ({ proposal, busy, onApprove, onReject }) => {
  const [note, setNote] = useState('');

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4">
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <Chip className={KIND_STYLES[proposal.kind] || KIND_STYLES.FOLLOW_UP}>
          {proposal.kind?.replace(/_/g, ' ')}
        </Chip>
        <Chip className="border-zinc-700 bg-zinc-800/60 text-zinc-400">
          {proposal.channel}
        </Chip>
        <span className="text-sm text-zinc-400">
          to{' '}
          <span className="font-semibold text-zinc-200">
            {proposal.to_person_id}
          </span>
        </span>
        <span className="ml-auto font-mono text-[11px] text-zinc-600">
          {proposal.reason_code}
        </span>
      </div>

      <p className="mb-3 text-sm leading-relaxed text-zinc-200">
        {proposal.message}
      </p>

      {/* The grounding quote is the point of review: it is how a person judges
          whether the follow-up is fair before approving it. */}
      {proposal.evidence?.quote && (
        <blockquote className="mb-3 border-l-2 border-zinc-700 pl-3 text-sm italic text-zinc-400">
          “{proposal.evidence.quote}”
          {proposal.evidence.seg_id && (
            <span className="ml-2 font-mono text-[11px] not-italic text-zinc-600">
              {proposal.evidence.seg_id}
            </span>
          )}
        </blockquote>
      )}
      {proposal.evidence?.activity_reference && (
        <p className="mb-3 text-xs text-zinc-500">
          Observed: {proposal.evidence.activity_reference}
        </p>
      )}

      {onApprove && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Note (optional)"
            className="min-w-[10rem] flex-1 rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:border-[#5652CF] focus:outline-none"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => onApprove(proposal.proposal_id, note)}
            className="flex items-center gap-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-sm font-semibold text-emerald-400 transition-all hover:bg-emerald-500/20 active:scale-95 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onReject(proposal.proposal_id, note)}
            className="rounded-xl border border-zinc-700 px-3 py-1.5 text-sm font-semibold text-zinc-300 transition-all hover:bg-zinc-800 active:scale-95 disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
};

const ClarificationCard = ({ item, busy, onAnswer }) => {
  const [resolution, setResolution] = useState('');
  const [entityRef, setEntityRef] = useState('');
  const [personId, setPersonId] = useState('');

  // TERM and ALIAS answers are promoted to the project glossary as
  // HUMAN_CONFIRMED; the rest are recorded against this meeting only. Saying
  // which is which stops people wondering why one answer taught the system.
  const teachesGlossary = ['TERM', 'ALIAS'].includes(item.ambiguity_type);
  const canSubmit = resolution.trim() && personId.trim() && !busy;

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4">
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <Chip className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300">
          {item.ambiguity_type || 'UNKNOWN'}
        </Chip>
        {item.urgency && (
          <Chip className="border-zinc-700 bg-zinc-800/60 text-zinc-400">
            {item.urgency}
          </Chip>
        )}
        <span className="ml-auto font-mono text-[11px] text-zinc-600">
          {item.seg_id}
        </span>
      </div>

      <p className="mb-1 text-sm font-semibold text-zinc-100">
        {item.question_text}
      </p>
      <p className="mb-3 text-sm text-zinc-400">
        Unclear term:{' '}
        <span className="font-mono text-zinc-200">{item.ambiguous_text}</span>
      </p>

      {item.surrounding_context && (
        <blockquote className="mb-3 border-l-2 border-zinc-700 pl-3 text-sm italic text-zinc-400">
          {item.surrounding_context}
        </blockquote>
      )}

      {item.candidate_meanings?.length > 0 && (
        <p className="mb-3 text-sm text-zinc-400">
          <span className="font-semibold text-zinc-300">Candidates:</span>{' '}
          {item.candidate_meanings
            .map(
              (candidate) =>
                candidate.ref || candidate.label || JSON.stringify(candidate),
            )
            .join(', ')}
        </p>
      )}

      {onAnswer && (
        <div className="grid gap-2 sm:grid-cols-3">
          <input
            type="text"
            value={resolution}
            onChange={(event) => setResolution(event.target.value)}
            placeholder="What does it mean? (required)"
            className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:border-[#5652CF] focus:outline-none sm:col-span-2"
          />
          <input
            type="text"
            value={personId}
            onChange={(event) => setPersonId(event.target.value)}
            placeholder="Resolved by (person ID)"
            className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:border-[#5652CF] focus:outline-none"
          />
          <input
            type="text"
            value={entityRef}
            onChange={(event) => setEntityRef(event.target.value)}
            placeholder="Entity ref, e.g. JIRA-4410 (optional)"
            className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:border-[#5652CF] focus:outline-none sm:col-span-2"
          />
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              onAnswer(item.clarification_id, {
                resolution: resolution.trim(),
                resolvedEntityRef: entityRef.trim(),
                resolvedByPersonId: personId.trim(),
              })
            }
            className="rounded-xl bg-[#5652CF] px-3 py-1.5 text-sm font-semibold text-white transition-all hover:bg-[#4845b5] active:scale-95 disabled:opacity-50"
          >
            Answer
          </button>
        </div>
      )}

      {teachesGlossary && (
        <p className="mt-2 text-[11px] text-zinc-500">
          Promoted to the project glossary as HUMAN_CONFIRMED, so the pipeline
          will not ask again.
        </p>
      )}
    </div>
  );
};

const EmptyState = ({ icon: Icon, children }) => (
  <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-zinc-800 py-16 text-center">
    <Icon className="h-8 w-8 text-zinc-700" />
    <p className="max-w-sm text-sm text-zinc-500">{children}</p>
  </div>
);

const DeliveryReceipt = ({ proposal, busy, onRecord }) => {
  const [reference, setReference] = useState('');
  return (
    <form
      className="rounded-lg border border-zinc-800 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (reference.trim()) onRecord(proposal.proposal_id, reference.trim());
      }}
    >
      <p className="mb-3 break-words text-sm text-zinc-300">
        {proposal.message}
      </p>
      <label className="block text-xs text-zinc-400">
        Delivered message reference
        <input
          required
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 p-2 text-sm text-zinc-100"
        />
      </label>
      <button
        type="submit"
        disabled={busy || !reference.trim()}
        className="mt-3 flex items-center gap-2 text-sm text-emerald-400 disabled:opacity-50"
      >
        <CheckCircle2 size={16} />
        Record delivery
      </button>
    </form>
  );
};

const MeetingApprovalsPanel = ({ accessToken, meetingId = null, queues }) => {
  // The parent may already own the queues (so its tab can show a count).
  // Falling back to a local hook keeps the panel usable on its own.
  const local = useMeetingApprovals(accessToken, meetingId);
  const state = queues || local;
  const {
    proposals,
    setProposals,
    clarifications,
    setClarifications,
    loading,
    error,
    setError,
    reload,
  } = state;

  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const attentionReviews = state.attentionReviews || [];
  // Approving, rejecting and answering need meeting:manage; everyone else
  // reads the same cards without their action rows.
  const canManage = useAccess().can('meeting:manage');
  const approvedContacts = state.approvedContacts || [];
  const recordDelivery = async (proposalId, reference) => {
    setBusyId(proposalId);
    setError(null);
    try {
      await recordProposalDelivery(accessToken, proposalId, reference);
      await reload();
    } catch (deliveryError) {
      setError(String(deliveryError?.message || deliveryError));
    } finally {
      setBusyId(null);
    }
  };
  const reviewEntity = async (entity, decision) => {
    setBusyId(entity.entity_id);
    setError(null);
    try {
      await reviewAttentionEntity(accessToken, entity, decision);
      await reload();
    } catch (reviewError) {
      setError(String(reviewError?.message || reviewError));
    } finally {
      setBusyId(null);
    }
  };

  const decide = async (decider, proposalId, note, verb) => {
    setBusyId(proposalId);
    setError(null);
    setNotice(null);
    try {
      await decider(accessToken, proposalId, note || null);
      setProposals((current) =>
        current.filter((item) => item.proposal_id !== proposalId),
      );
      setNotice(
        `Proposal ${verb}. This records a decision — nothing was sent.`,
      );
      await reload();
    } catch (decideError) {
      // A 409 means another reviewer decided first, so the list is stale.
      setError(String(decideError?.message || decideError));
      reload();
    } finally {
      setBusyId(null);
    }
  };

  const submitAnswer = async (clarificationId, answer) => {
    setBusyId(clarificationId);
    setError(null);
    setNotice(null);
    try {
      await answerClarification(accessToken, clarificationId, answer);
      setClarifications((current) =>
        current.filter((item) => item.clarification_id !== clarificationId),
      );
      setNotice('Answer recorded.');
    } catch (answerError) {
      setError(String(answerError?.message || answerError));
      reload();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-[#5652CF]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <ReadOnlyNotice permission="meeting:manage">
        You can read these items. Only a team lead or a project manager can
        approve, reject or answer them.
      </ReadOnlyNotice>
      <div className="flex items-start gap-3">
        <p className="max-w-2xl text-sm text-zinc-500">
          Follow-ups the tracker drafted, and questions the pipeline could not
          answer for itself. Approving records a decision — this release has no
          send path, so nothing leaves the system.
        </p>
        <button
          type="button"
          onClick={reload}
          className="ml-auto flex shrink-0 items-center gap-1.5 rounded-xl border border-zinc-800 px-3 py-1.5 text-sm text-zinc-400 transition-all hover:bg-zinc-800 hover:text-zinc-200"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {notice && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {notice}
        </div>
      )}

      {canManage && approvedContacts.length > 0 && (
        <section>
          <h3 className="mb-3 text-sm font-semibold text-zinc-300">
            Approved follow-ups awaiting delivery
          </h3>
          <div className="flex flex-col gap-3">
            {approvedContacts.map((proposal) => (
              <DeliveryReceipt
                key={proposal.proposal_id}
                proposal={proposal}
                busy={busyId === proposal.proposal_id}
                onRecord={recordDelivery}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-3 text-sm font-semibold text-zinc-300">
          Priority reviews ({attentionReviews.length})
        </h3>
        <div className="flex flex-col gap-3">
          {attentionReviews.map((entity) => (
            <article
              key={`${entity.collection}:${entity.entity_id}`}
              className="rounded-lg border border-zinc-800 p-4"
            >
              <p className="break-words text-sm text-zinc-200">
                {entity.normalised_text ||
                  entity.description ||
                  entity.decision_text ||
                  entity.claim_text ||
                  entity.entity_id}
              </p>
              <AttentionSummary attention={entity.attention} />
              {entity.evidence?.quote && (
                <blockquote className="my-3 break-words border-l border-zinc-600 pl-3 text-sm text-zinc-400">
                  {entity.evidence.quote}
                </blockquote>
              )}
              {canManage && (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={busyId === entity.entity_id}
                    onClick={() => reviewEntity(entity, 'ACCEPTED')}
                    className="flex items-center gap-2 text-sm text-emerald-400 disabled:opacity-50"
                  >
                    <CheckCircle2 size={16} />
                    Accept
                  </button>
                  <button
                    type="button"
                    disabled={busyId === entity.entity_id}
                    onClick={() => reviewEntity(entity, 'REJECTED')}
                    className="flex items-center gap-2 text-sm text-zinc-400 disabled:opacity-50"
                  >
                    <X size={16} />
                    Reject
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-300">
          <Send className="h-4 w-4 text-zinc-500" />
          Follow-up proposals
          <span className="rounded-lg bg-zinc-800 px-2 py-0.5 text-[11px] text-zinc-400">
            {proposals.length}
          </span>
        </h3>
        {proposals.length === 0 ? (
          <EmptyState icon={Send}>
            Nothing awaiting approval. The tracker adds a proposal when a
            promise goes quiet past its deadline.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-3">
            {proposals.map((proposal) => (
              <ProposalCard
                key={proposal.proposal_id}
                proposal={proposal}
                busy={busyId === proposal.proposal_id}
                onApprove={
                  canManage
                    ? (id, note) =>
                        decide(approveProposal, id, note, 'approved')
                    : undefined
                }
                onReject={(id, note) =>
                  decide(rejectProposal, id, note, 'rejected')
                }
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-300">
          <HelpCircle className="h-4 w-4 text-zinc-500" />
          Clarifications
          <span className="rounded-lg bg-zinc-800 px-2 py-0.5 text-[11px] text-zinc-400">
            {clarifications.length}
          </span>
        </h3>
        {clarifications.length === 0 ? (
          <EmptyState icon={HelpCircle}>
            No open questions. The pipeline raises one when a load-bearing term
            has no resolvable meaning.
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-3">
            {clarifications.map((item) => (
              <ClarificationCard
                key={item.clarification_id}
                item={item}
                busy={busyId === item.clarification_id}
                onAnswer={canManage ? submitAnswer : undefined}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MeetingApprovalsPanel;
